import { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'
import { useAuth0 } from '@/hooks/useAuth0'
import api from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const isDev = import.meta.env?.MODE === 'development' || process.env.NODE_ENV === 'development'

export default function LoginPage() {
  const { user, devSignIn } = useAuth()
  const { isAuthenticated: isAuth0Authenticated, isLoading: auth0Loading, loginWithRedirect } = useAuth0()
  const location = useLocation()
  const [checkingConfig, setCheckingConfig] = useState(true)

  // Check if Auth0 is configured
  useEffect(() => {
    async function checkAuth0Config() {
      try {
        await api.get('/auth/config')
      } catch {
        // Auth0 config check failed - still show login screen
      } finally {
        setCheckingConfig(false)
      }
    }
    checkAuth0Config()
  }, [])

  // Redirect if already authenticated
  if (user || isAuth0Authenticated) {
    const from = location.state?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  // Shared header with logo and title
  const pageHeader = (
    <div className="text-center">
      <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
        <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
      <h2 className="mt-6 text-3xl font-bold text-gray-900">
        Operational Resilience
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Secure access to your resilience dashboard
      </p>
    </div>
  )

  // Show loading only while checking Auth0 config
  if (checkingConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {pageHeader}
          <div className="text-center">
            <div className="h-8 w-8 mx-auto">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
            <p className="text-gray-600 mt-4">Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {pageHeader}

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-semibold text-center text-gray-900">Welcome</CardTitle>
            <CardDescription className="text-center text-gray-600">
              Enter your credentials to access your resilience dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-8">
            <div className="space-y-4">
              <button
                onClick={() => loginWithRedirect()}
                disabled={auth0Loading}
                className="w-full h-11 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center"
              >
                {auth0Loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Redirecting to login...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
              <p className="text-sm text-center text-gray-500">
                You will be redirected to our secure authentication page
              </p>

              {isDev && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-amber-600 text-center mb-3 font-medium">
                    Development Mode Only
                  </p>
                  <button
                    onClick={devSignIn}
                    className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center"
                  >
                    Dev Sign In (Bypass Auth)
                  </button>
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Signs in as a super-admin dev user without backend authentication
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
